import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "dentiprime_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            /* ignore quota errors */
        }
    }, [items]);

    const addItem = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.id === product.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
                return copy;
            }
            return [
                ...prev,
                {
                    id: product.id,
                    name: product.name,
                    category: product.category,
                    quantity: qty,
                },
            ];
        });
    }, []);

    const updateQty = useCallback((id, qty) => {
        setItems((prev) =>
            prev
                .map((p) => (p.id === id ? { ...p, quantity: Math.max(1, qty) } : p))
                .filter((p) => p.quantity > 0),
        );
    }, []);

    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const totalQty = useMemo(
        () => items.reduce((s, i) => s + i.quantity, 0),
        [items],
    );

    const value = useMemo(
        () => ({
            items,
            totalQty,
            open,
            setOpen,
            addItem,
            updateQty,
            removeItem,
            clear,
        }),
        [items, totalQty, open, addItem, updateQty, removeItem, clear],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
